import { useEffect, useState } from 'react'
import { isString } from 'lodash'

import { importRecipeFromLink } from '../../api/recipe'

import '../ScanRecipeCard/style.css'
import './style.css'

const GENERIC_ERROR =
  "We couldn't get the recipe from that link. Please try again, or type the recipe in below."

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

// Paste a link to a recipe on a website and we fill in the recipe. Only the name,
// ingredients, steps and servings come across: the site's photos and description stay
// on the site, and the recipe keeps a credit and a link back to it.
// Shown as a small tile until the cook taps it.
// startOpen: arrived from "Paste a link" on the profile or in an email, so the box is open
const RecipeFromLink = ({
  onImported,
  onActiveChange = () => {},
  hidden = false,
  startOpen = false,
}) => {
  const [link, setLink] = useState('')
  const [status, setStatus] = useState(startOpen ? 'ready' : 'closed')
  const [error, setError] = useState('')
  const [summary, setSummary] = useState(null)

  const getRecipe = async () => {
    if (!link.trim()) {
      setError('Please paste the link to the recipe first.')
      setStatus('error')
      return
    }
    setStatus('reading')
    setError('')
    try {
      const { data } = await importRecipeFromLink(link.trim())
      onImported(data)
      setSummary({
        ingredients: data?.ingredients?.length || 0,
        steps: data?.steps?.length || 0,
        sourceName: data?.source_name,
      })
      setStatus('done')
    } catch (err) {
      const message = err?.response?.data?.data
      setError(isString(message) ? message : GENERIC_ERROR)
      setStatus('error')
    }
  }

  const startOver = () => {
    setLink('')
    setSummary(null)
    setError('')
    setStatus('closed')
  }

  // Tell the page when this tool is open, so it can hide the other "skip the typing" tile
  const isOpen = status !== 'closed'
  useEffect(() => onActiveChange(isOpen), [isOpen])

  if (hidden) {
    return null
  }

  if (!isOpen) {
    return (
      <button type="button" className="quickTile" onClick={() => setStatus('ready')}>
        <span className="quickTileIcon" aria-hidden="true">
          <WebRecipeArt />
        </span>
        <span className="quickTileTitle">Paste a link</span>
        <span className="quickTileText">Import a recipe from any website.</span>
      </button>
    )
  }

  if (status === 'reading') {
    return (
      <div className="quickPanel">
        <div className="scanCardReading" role="status">
          <span className="scanCardSpinner" aria-hidden="true" />
          <p className="scanCardText">
            Getting the recipe from the website. This can take a minute.
          </p>
        </div>
      </div>
    )
  }

  if (status === 'done') {
    return (
      <div className="quickPanel quickPanelDone">
        <p className="scanCardTitle">
          <span className="quickDoneMark" aria-hidden="true">
            ✓
          </span>
          Filled in from {summary?.sourceName || 'the website'}
        </p>
        <p className="scanCardText">
          We found {plural(summary?.ingredients, 'ingredient')} and {plural(summary?.steps, 'step')}{' '}
          for the next steps, and we'll credit and link the original.
        </p>
        <p className="scanCardText scanCardNote">
          Two things are up to you: write the summary below in your own words, and once you've made
          it, add a photo of your own dish. We never copy a website's photos or words.
        </p>
        <button type="button" className="scanCardLink" onClick={startOver}>
          Use a different link
        </button>
      </div>
    )
  }

  return (
    // Not a <form>: the recipe page around it already is one
    <div className="quickPanel">
      <p className="scanCardTitle">Paste a recipe link</p>
      <div className="linkRecipeRow">
        <label className="linkRecipeLabel" htmlFor="recipeFromLinkInput">
          Link to the recipe
        </label>
        <input
          id="recipeFromLinkInput"
          className="linkRecipeInput"
          type="url"
          inputMode="url"
          autoComplete="off"
          // The cook just tapped "Paste a recipe link", so put them straight in the box
          autoFocus
          placeholder="https://www.example.com/best-banana-bread"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          onKeyDown={(e) => {
            // Enter gets the recipe instead of saving the whole recipe form
            if (e.key === 'Enter') {
              e.preventDefault()
              getRecipe()
            }
          }}
        />
        <button type="button" className="scanCardButton" onClick={getRecipe}>
          {status === 'error' ? 'Try again' : 'Get the recipe'}
        </button>
      </div>
      {status === 'error' && (
        <p className="scanCardText scanCardError linkRecipeMessage" role="alert">
          {error}
        </p>
      )}
      <button type="button" className="scanCardLink linkRecipeCancel" onClick={startOver}>
        Cancel
      </button>
    </div>
  )
}

// A web page with the Morsel fork, coming into the app
export const WebRecipeArt = () => (
  <svg viewBox="0 0 64 64" width="56" height="56">
    <rect
      x="5"
      y="9"
      width="52"
      height="42"
      rx="6"
      fill="#fffdf2"
      stroke="#37190c"
      strokeWidth="2.5"
    />
    <path d="M5 19h52" stroke="#37190c" strokeWidth="2.5" />
    <circle cx="11.5" cy="14" r="1.8" fill="#d9483b" />
    <circle cx="17.5" cy="14" r="1.8" fill="#fec925" />
    <circle cx="23.5" cy="14" r="1.8" fill="#57ad91" />
    <g transform="translate(24 23) scale(0.1)" fill="#57ad91">
      <circle cx="61" cy="56" r="52" />
      <path d="M21 106l16 8v73H21z" />
      <path d="M53 120h16v67H53z" />
      <path d="M85 114l16-8v99a24 24 0 0 1-24 24h-8v48H53v-64h24a8 8 0 0 0 8-8z" />
    </g>
    <circle cx="50" cy="50" r="10" fill="#fec925" stroke="#37190c" strokeWidth="2.5" />
    <path
      d="M46 50h8M51 46.5l3.5 3.5-3.5 3.5"
      fill="none"
      stroke="#37190c"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export default RecipeFromLink
